import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Edit, Trash2, Building2, Target, TrendingUp, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProjectManagement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Project states
  const [projects, setProjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Campaign states
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignFormData, setCampaignFormData] = useState({
    campaign_name: '',
    campaign_id: '',
    form_name: '',
    form_id: '',
    platform: '',
    project_id: ''
  });
  const [campaignPerformance, setCampaignPerformance] = useState(null);
  const [performanceDialogOpen, setPerformanceDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('projects');
  
  // Campaign bulk upload states
  const [campaignFile, setCampaignFile] = useState(null);
  const [uploadingCampaigns, setUploadingCampaigns] = useState(false);

  useEffect(() => {
    if (user?.user_role !== 'Super Administrator' && user?.user_role !== 'Manager') {
      navigate('/');
      return;
    }
    loadProjects();
    loadCampaigns();
  }, [user]);

  const loadProjects = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = () => {
    setEditingProject(null);
    setProjectName('');
    setDialogOpen(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectName(project.project_name);
    setDialogOpen(true);
  };

  const handleSaveProject = async () => {
    if (!projectName.trim()) {
      toast.error('Project name is required');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      if (editingProject) {
        // Update existing project
        await axios.put(
          `${API}/projects/${editingProject.id}`,
          { project_name: projectName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Project updated successfully');
      } else {
        // Create new project
        await axios.post(
          `${API}/projects`,
          { project_name: projectName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Project created successfully');
      }
      setDialogOpen(false);
      loadProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleToggleActive = async (projectId, currentStatus) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${API}/projects/${projectId}`,
        { active_status: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Project status updated');
      loadProjects();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted successfully');
      loadProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  // Campaign management functions
  const loadCampaigns = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    }
  };

  const handleAddCampaign = () => {
    setEditingCampaign(null);
    setCampaignFormData({
      campaign_name: '',
      campaign_id: '',
      form_name: '',
      form_id: '',
      platform: '',
      project_id: ''
    });
    setCampaignDialogOpen(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignFormData({
      campaign_name: campaign.campaign_name,
      campaign_id: campaign.campaign_id,
      form_name: campaign.form_name,
      form_id: campaign.form_id,
      platform: campaign.platform,
      project_id: campaign.project_id
    });
    setCampaignDialogOpen(true);
  };

  const handleSaveCampaign = async () => {
    if (!campaignFormData.campaign_name.trim() || !campaignFormData.campaign_id.trim() || 
        !campaignFormData.form_name.trim() || !campaignFormData.form_id.trim() || 
        !campaignFormData.platform.trim() || !campaignFormData.project_id.trim()) {
      toast.error('All campaign fields are required');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      if (editingCampaign) {
        // Update existing campaign
        await axios.put(
          `${API}/campaigns/${editingCampaign.id}`,
          campaignFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Campaign updated successfully');
      } else {
        // Create new campaign
        await axios.post(
          `${API}/campaigns`,
          campaignFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Campaign created successfully');
      }
      setCampaignDialogOpen(false);
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId, campaignName) => {
    if (!window.confirm(`Are you sure you want to delete "${campaignName}"? This action cannot be undone.`)) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campaign deleted successfully');
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
    }
  };

  const handleViewPerformance = async (campaign) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.id}/performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaignPerformance(response.data);
      setPerformanceDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load campaign performance');
    }
  };

  // Campaign bulk upload functions
  const downloadCampaignTemplate = () => {
    const csvContent = `campaign_name,campaign_id,form_name,form_id,platform,project_name
Marketing Campaign Q1,META_CAMP_001,Lead Gen Form,FORM_001,Facebook,Aarambh Greens
Google Ads Campaign,GOOGLE_CAMP_001,Contact Form,FORM_002,Google,Aarambh Prime
Instagram Campaign,INSTA_CAMP_001,Interest Form,FORM_003,Instagram,Samesh`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'campaign_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleBulkUploadCampaigns = async () => {
    if (!campaignFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploadingCampaigns(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', campaignFile);

    try {
      const response = await axios.post(`${API}/campaigns/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const { campaigns_created, campaigns_skipped, errors, total_rows } = response.data;
      
      if (errors && errors.length > 0) {
        toast.error(`Upload completed with errors. Created: ${campaigns_created}, Skipped: ${campaigns_skipped}, Errors: ${errors.length}`);
        console.error('Upload errors:', errors);
      } else {
        toast.success(`Successfully uploaded ${campaigns_created} campaigns. Skipped ${campaigns_skipped} duplicates.`);
      }
      
      setCampaignFile(null);
      loadCampaigns(); // Refresh the campaigns list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload campaigns');
    } finally {
      setUploadingCampaigns(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="project-management-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-button">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                  Project & Campaign Management
                </h1>
                <p className="text-sm text-slate-600">
                  {projects.length} projects • {campaigns.length} campaigns
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="projects" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Projects ({projects.length})
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Campaigns ({campaigns.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-800">All Projects</h2>
              <Button
                onClick={handleAddProject}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="add-project-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Project
              </Button>
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {projects.map((project) => (
                <Card key={project.id} data-testid={`project-card-${project.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1">
                        <Building2 className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                        <h3 className="font-semibold text-slate-800 text-sm break-words">
                          {project.project_name}
                        </h3>
                      </div>
                      <Switch
                        checked={project.active_status}
                        onCheckedChange={() => handleToggleActive(project.id, project.active_status)}
                        data-testid={`active-toggle-${project.id}`}
                      />
                    </div>

                    <div className="mb-3">
                      <Badge
                        className={project.active_status ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}
                      >
                        {project.active_status ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditProject(project)}
                        className="flex-1"
                        data-testid={`edit-project-${project.id}`}
                      >
                        <Edit className="mr-1 h-3 w-3" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteProject(project.id, project.project_name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {projects.length === 0 && (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Projects Yet</h3>
                <p className="text-slate-600 mb-4">Get started by adding your first project</p>
                <Button onClick={handleAddProject} className="bg-cyan-600 hover:bg-cyan-700">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Project
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="campaigns">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-800">All Campaigns</h2>
              <div className="flex gap-2">
                {/* Campaign Bulk Upload */}
                <div className="flex gap-2">
                  <input
                    type="file"
                    id="campaign-file-upload"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setCampaignFile(e.target.files[0])}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={downloadCampaignTemplate}
                    size="sm"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Template
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('campaign-file-upload').click()}
                    size="sm"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {campaignFile ? campaignFile.name.substring(0, 10) + '...' : 'Upload CSV'}
                  </Button>
                  {campaignFile && (
                    <Button
                      onClick={handleBulkUploadCampaigns}
                      disabled={uploadingCampaigns}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {uploadingCampaigns ? 'Uploading...' : 'Process File'}
                    </Button>
                  )}
                </div>
                <Button
                  onClick={handleAddCampaign}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="add-campaign-button"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Campaign
                </Button>
              </div>
            </div>

            {/* Campaigns Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map((campaign) => (
                <Card key={campaign.id} data-testid={`campaign-card-${campaign.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1">
                        <Target className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        <h3 className="font-semibold text-slate-800 text-sm break-words">
                          {campaign.campaign_name}
                        </h3>
                      </div>
                    </div>

                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">Platform:</span>
                        <Badge variant="outline">{campaign.platform}</Badge>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">Project:</span>
                        <span className="text-slate-800 font-medium">{campaign.project_name}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">Campaign ID:</span>
                        <span className="text-slate-800 font-mono text-xs">{campaign.campaign_id}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">Form:</span>
                        <span className="text-slate-800">{campaign.form_name}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewPerformance(campaign)}
                        className="flex-1"
                        data-testid={`performance-${campaign.id}`}
                      >
                        <TrendingUp className="mr-1 h-3 w-3" />
                        Performance
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditCampaign(campaign)}
                        data-testid={`edit-campaign-${campaign.id}`}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteCampaign(campaign.id, campaign.campaign_name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-campaign-${campaign.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {campaigns.length === 0 && (
              <div className="text-center py-12">
                <Target className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Campaigns Yet</h3>
                <p className="text-slate-600 mb-4">Get started by adding your first campaign</p>
                <Button onClick={handleAddCampaign} className="bg-cyan-600 hover:bg-cyan-700">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Campaign
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name</Label>
              <Input
                id="project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name"
                data-testid="project-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveProject}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-project-button"
            >
              {editingProject ? 'Update' : 'Create'} Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Campaign Dialog */}
      <Dialog open={campaignDialogOpen} onOpenChange={setCampaignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Add New Campaign'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="campaign-name">Campaign Name</Label>
              <Input
                id="campaign-name"
                value={campaignFormData.campaign_name}
                onChange={(e) => setCampaignFormData({...campaignFormData, campaign_name: e.target.value})}
                placeholder="Enter campaign name"
                data-testid="campaign-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="campaign-id">Campaign ID</Label>
              <Input
                id="campaign-id"
                value={campaignFormData.campaign_id}
                onChange={(e) => setCampaignFormData({...campaignFormData, campaign_id: e.target.value})}
                placeholder="External campaign ID (e.g., FB_CAMP_123)"
                data-testid="campaign-id-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="form-name">Form Name</Label>
              <Input
                id="form-name"
                value={campaignFormData.form_name}
                onChange={(e) => setCampaignFormData({...campaignFormData, form_name: e.target.value})}
                placeholder="Lead form name"
                data-testid="form-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="form-id">Form ID</Label>
              <Input
                id="form-id"
                value={campaignFormData.form_id}
                onChange={(e) => setCampaignFormData({...campaignFormData, form_id: e.target.value})}
                placeholder="External form ID"
                data-testid="form-id-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="platform">Platform</Label>
              <Select 
                value={campaignFormData.platform} 
                onValueChange={(value) => setCampaignFormData({...campaignFormData, platform: value})}
              >
                <SelectTrigger data-testid="platform-select">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Facebook">Facebook</SelectItem>
                  <SelectItem value="Google">Google</SelectItem>
                  <SelectItem value="Instagram">Instagram</SelectItem>
                  <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                  <SelectItem value="Twitter">Twitter</SelectItem>
                  <SelectItem value="YouTube">YouTube</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project">Project</Label>
              <Select 
                value={campaignFormData.project_id} 
                onValueChange={(value) => setCampaignFormData({...campaignFormData, project_id: value})}
              >
                <SelectTrigger data-testid="project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.filter(p => p.active_status).map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.project_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCampaignDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveCampaign}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-campaign-button"
            >
              {editingCampaign ? 'Update' : 'Create'} Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Performance Dialog */}
      <Dialog open={performanceDialogOpen} onOpenChange={setPerformanceDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Campaign Performance</DialogTitle>
          </DialogHeader>
          {campaignPerformance && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Campaign</Label>
                  <p className="font-semibold">{campaignPerformance.campaign_name}</p>
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Platform</Label>
                  <p className="font-semibold">{campaignPerformance.platform}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{campaignPerformance.total_leads}</p>
                  <p className="text-sm text-slate-600">Total Leads</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{campaignPerformance.conversion_metrics.booked_leads}</p>
                  <p className="text-sm text-slate-600">Booked</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{campaignPerformance.conversion_metrics.conversion_rate}%</p>
                  <p className="text-sm text-slate-600">Conversion</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Status Breakdown</Label>
                <div className="space-y-1">
                  {Object.entries(campaignPerformance.status_breakdown).map(([status, count]) => (
                    <div key={status} className="flex justify-between text-sm">
                      <span>{status}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPerformanceDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProjectManagement;
